/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component, createRef, Fragment } from "react";
import PropTypes from "prop-types";
import { JOBS_SUB_MENU } from "@constants";
import { ClientJobRequestService } from "@services";
import utils from "@utils";
import { ConfirmationModal, Loader, ScrollDetect, TabMenu } from "../../../components";

import "./JobRequests.scss";

class JobRequests extends Component {
	constructor(props) {
		super(props);

		this.state = {
			pageNumber: 1,
			data: [],
			hasMore: false,
		};

		this._confirmationModal = createRef();
	}

	/** Retrieves a paginated list of job requests for the current user */
	getJobRequests = () => {
		const { pageNumber } = this.state;
		ClientJobRequestService.getJobRequests(pageNumber)
			.then(({ data }) => {
				const newData = data.ItemList || [];
				this.setState(prevState => ({
					data: [...prevState.data, ...newData].sort((a, b) => a.Id - b.Id),
					pageNumber: prevState.pageNumber + 1,
					hasMore: data.Count > data.DisplayCount * pageNumber,
				}));
			})
			.catch(() => {
				this.setState({ data: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: "Unable to load job requests",
					timeOut: 1500,
				});
			});
	};

	/**
	 * Confirms the user's intention to delete a job request and updates the UI after completion
	 * @param {Number} requestId The ID number for the job request
	 * @param {String} organizationName The name of the organization that requested the job
	 * @returns {(event:Event) => void}
	 */
	removeRequest = (requestId, organizationName) => event => {
		if (event) event.stopPropagation();
		const { lang } = global;

		this._confirmationModal.current.openModal({
			title: "Delete Job Request",
			message: `Are you sure you want to delete this job request for ${organizationName}?`,
			confirmButton: "Yes",
			onConfirm: () => {
				this.setState(prevState => ({
					data: prevState.data.map(item =>
						item.Id === requestId ? { ...item, itemLoading: true } : item,
					),
				}));

				ClientJobRequestService.removeJobRequest(requestId)
					.then(() => {
						this.setState(prevState => ({
							data: prevState.data.filter(item => item.Id !== requestId),
						}));

						utils.notify({
							type: "success",
							message: lang.t("Job request deleted successfully"),
							timeOut: 1500,
						});
					})
					.catch(() => {
						this.setState(prevState => ({
							data: prevState.data.map(item =>
								item.Id === requestId ? { ...item, itemLoading: false } : item,
							),
						}));

						utils.notify({
							type: "error",
							message: "Unable to remove job request",
							timeOut: 1500,
						});
					});
			},
		});
	};

	/**
	 * Navigates the user to the job form to create a new job with the provided details
	 * @param {Number} requestId The ID number for the job request
	 */
	createJobFromRequest = requestId => {
		const { data } = this.state;
		const jobRequest = data.find(item => item.Id === requestId);

		if (!jobRequest.itemLoading) {
			const {
				router: { push },
			} = this.props;
			push(`/jobs/add?requestId=${requestId}`);
		}
	};

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = JOBS_SUB_MENU.find(tab => tab.type === "requests");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	render() {
		const { data, hasMore } = this.state;
		const { user } = this.props;
		const { lang } = global;

		return (
			<Fragment>
				<TabMenu
					tabs={JOBS_SUB_MENU}
					activeTab={JOBS_SUB_MENU.find(tab => tab.type === "requests").tabNumber}
					onClickTab={this.onClickTab}
					user={user}
				/>
				<div
					id="jobrequests-list-container"
					className="container"
				>
					<div
						id="jobrequests-list-header"
						className="row hidden-xs"
					>
						<div className="col-sm-3 jobrequests-list-header-item">
							{lang.t("Client")}
						</div>
						<div className="col-sm-2 jobrequests-list-header-item">
							{lang.t("User")}
						</div>
						<div className="col-sm-3 jobrequests-list-header-item">
							{lang.t("Job Date")}
						</div>
						<div className="col-sm-2 jobrequests-list-header-item">
							{`${lang.t("Requested")} ${lang.t("Date")}`}
						</div>
						<div className="col-sm-1 jobrequests-list-header-item">
							{lang.t("Estimate")}
						</div>
						<div className="col-sm-1" />
					</div>
					<div
						id="jobrequests-list-body"
						className="container"
					>
						<For
							each="item"
							index="index"
							of={data}
						>
							<div
								role="row"
								tabIndex={index}
								className="row card jobrequest-row v-align-sm"
								onClick={() => this.createJobFromRequest(item.Id)}
								key={`jobrequests_item_${item.Id}`}
							>
								<div
									className="col-xs-6 col-sm-3 jobrequest-client"
									title={item.OrganizationName || "Client Name"}
								>
									{item.OrganizationName || ""}
								</div>
								<div
									className="col-xs-6 col-sm-2 jobrequest-username"
									title={`${item.UserFirstName} ${item.UserLastName}`}
								>
									{`${item.UserFirstName} ${item.UserLastName}`}
								</div>
								<div className="col-sm-3">
									{utils.formatDate(
										item.StartDateTime,
										"en-US",
										user.TimeZoneId,
										"numeric",
									)}
									<br />
									{utils.formatTime(item.StartDateTime, true, user.TimeZoneId)}
									&nbsp;-&nbsp;
									{utils.formatTime(item.EndDateTime, true, user.TimeZoneId)}
								</div>
								<div className="col-sm-2">
									<span className="hidden-sm hidden-md hidden-lg">
										Requested&nbsp;
									</span>
									{utils.formatDate(
										`${item.CreatedDateUTC}Z`,
										"en-US",
										user.TimeZoneId,
										"numeric",
									)}
								</div>
								<div className="col-sm-1">
									<span className="hidden-sm hidden-md hidden-lg">
										Estimate:&nbsp;
									</span>
									{utils.currencyFormatter(item.Estimate)}
								</div>
								<div className="col-sm-1 jobrequest-btn-container">
									<Choose>
										<When condition={item.itemLoading}>
											<Loader
												active
												size="small"
												spinnerOnly
											/>
										</When>
										<Otherwise>
											<button
												type="button"
												id="jobrequests-list-actions-remove"
												aria-label="Remove job request"
												onClick={this.removeRequest(
													item.Id,
													item.OrganizationName,
												)}
												data-prevent-navigation
												className="btn btn-icon-only btn-primary"
											>
												<i className="rkf rkf-trash" />
											</button>
										</Otherwise>
									</Choose>
								</div>
							</div>
						</For>
						<If condition={hasMore}>
							<ScrollDetect
								className="text-center"
								onVisible={this.getJobRequests}
							>
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</ScrollDetect>
						</If>
					</div>
					<ConfirmationModal ref={this._confirmationModal} />
				</div>
			</Fragment>
		);
	}
}

JobRequests.propTypes = {
	user: PropTypes.object.isRequired,
	router: PropTypes.object.isRequired,
};

export default JobRequests;
